import React from "react";
import { Link } from "react-router-dom";
import LegalLayout from "../components/LegalLayout";
import { legal, has, officeOneLine, entityName } from "../data/legal";

const LAST_UPDATED = "25 June 2026";

export default function PrivacyPolicy() {
  const entity = entityName();
  const address = officeOneLine();
  const ContactLine = () =>
    has(legal.email) ? (
      <a href={`mailto:${legal.email}`}>{legal.email}</a>
    ) : (
      <Link to="/contact/">our contact page</Link>
    );

  const sections = [
    {
      heading: "About this policy",
      body: (
        <>
          <p>
            This Privacy Policy explains how {entity}
            {has(legal.abn) ? ` (ABN ${legal.abn})` : ""}, a migration support
            and coordination service, collects, uses, discloses, and protects
            your personal information. We are not a registered migration agent. We
            prepare your documentation and match you with a registered migration
            agent who provides the regulated advice and lodgement. We handle
            personal information in accordance with the Privacy Act 1988 (Cth) and
            the Australian Privacy Principles (APPs).
          </p>
          <p>
            By using this website or engaging our services, you agree to the
            handling of your personal information as described here.
          </p>
        </>
      ),
    },
    {
      heading: "The information we collect",
      body: (
        <>
          <p>
            The personal information we collect depends on the service you ask us
            for. It may include:
          </p>
          <ul>
            <li>Identity and contact details (name, date of birth, address, email, phone, nationality, passport details).</li>
            <li>Information about your visa enquiry (your goals, history, qualifications, employment, English test results, and family members).</li>
            <li>
              Sensitive information where it is relevant to a visa application,
              such as health information, biometric-related information, criminal
              history or police checks, and information about your relationships.
              We collect sensitive information only with your consent or where the
              law allows or requires it.
            </li>
            <li>Financial information relevant to an application (for example evidence of funds).</li>
            <li>Website usage and attribution data (see the cookies section below).</li>
          </ul>
        </>
      ),
    },
    {
      heading: "How we collect it",
      body: (
        <p>
          We collect personal information directly from you through our forms,
          our intake form, consultations, email, phone, and documents you
          provide. Where it is reasonable and practicable, we collect information
          from you directly. Sometimes we collect it from third parties you
          authorise, such as a sponsor, employer, or family member, or from
          government and assessing bodies in connection with your matter.
        </p>
      ),
    },
    {
      heading: "Why we collect and use it",
      body: (
        <>
          <p>We collect and use your personal information to:</p>
          <ul>
            <li>respond to your enquiry and understand how we can help;</li>
            <li>prepare and organise your documentation and get your file application-ready;</li>
            <li>match you with a registered migration agent and share your information with them so they can provide the regulated advice and lodgement;</li>
            <li>communicate with you and manage our relationship and our records;</li>
            <li>meet our legal and regulatory obligations;</li>
            <li>improve our website and services; and</li>
            <li>send you updates if you have opted in (see Marketing below).</li>
          </ul>
        </>
      ),
    },
    {
      heading: "Who we disclose it to",
      body: (
        <>
          <p>We may disclose your personal information to:</p>
          <ul>
            <li>the registered migration agent we match you with, so they can advise you and lodge your application. We do this with your consent, and their handling of your information is subject to their own privacy and professional obligations;</li>
            <li>the Department of Home Affairs and other Australian government agencies for your application;</li>
            <li>skills assessing authorities, panel physicians for health examinations, police-check providers, and translation services where relevant;</li>
            <li>a sponsor, employer, or education provider where you have authorised it;</li>
            <li>our professional service providers, including secure IT, document storage, and email providers, under confidentiality obligations; and</li>
            <li>others where you consent, or where we are required or authorised by law.</li>
          </ul>
          <p>We do not sell your personal information.</p>
        </>
      ),
    },
    {
      heading: "Overseas disclosure",
      body: (
        <p>
          Some of our service providers (for example secure cloud storage or
          email) may store or process information outside Australia. Where this
          happens, we take reasonable steps to ensure your information is handled
          consistently with the Australian Privacy Principles. Lodging an
          application may also necessarily involve disclosure to authorities
          outside Australia where your matter requires it.
        </p>
      ),
    },
    {
      heading: "Cookies, analytics, and attribution",
      body: (
        <p>
          Our website uses your browser's session storage to record how you first
          reached us (for example marketing campaign parameters in the web
          address) so we can understand which channels bring enquiries. This is
          first-party only and is attached to an enquiry if you submit one. We do
          not use it to sell your data. You can clear this at any time through
          your browser settings.
        </p>
      ),
    },
    {
      heading: "Security and storage",
      body: (
        <p>
          We take reasonable steps to protect your personal information from
          misuse, interference, loss, and unauthorised access, modification, or
          disclosure, including secured storage and access controls. No method of
          transmission or storage is completely secure, but we work to protect
          your information and to respond appropriately to any data breach,
          including our obligations under the Notifiable Data Breaches scheme.
        </p>
      ),
    },
    {
      heading: "How long we keep it",
      body: (
        <p>
          We keep your personal information for as long as needed to provide our
          services and to meet our record-keeping and legal obligations. When it
          is no longer required, we take reasonable steps to destroy or
          de-identify it.
        </p>
      ),
    },
    {
      heading: "Access and correction",
      body: (
        <p>
          You may request access to the personal information we hold about you and
          ask us to correct it if it is inaccurate, out of date, incomplete, or
          misleading. Contact us using the details below. We will respond within a
          reasonable time and may need to verify your identity first.
        </p>
      ),
    },
    {
      heading: "Marketing and consent",
      body: (
        <p>
          We only send marketing updates if you have opted in, and you can
          unsubscribe at any time using the link in our emails or by contacting
          us. Opting out of marketing does not affect communications about your
          enquiry or matter.
        </p>
      ),
    },
    {
      heading: "Complaints",
      body: (
        <p>
          If you have a concern about how we have handled your personal
          information, please contact us first using the details below and we will
          work to resolve it. If you are not satisfied, you may contact the Office
          of the Australian Information Commissioner (OAIC) at{" "}
          <a href="https://www.oaic.gov.au" target="_blank" rel="noreferrer">
            oaic.gov.au
          </a>
          .
        </p>
      ),
    },
    {
      heading: "Changes to this policy",
      body: (
        <p>
          We may update this policy from time to time. The current version is
          always available on this page, with the last-updated date shown above.
        </p>
      ),
    },
    {
      heading: "Contact us",
      body: (
        <p>
          For any privacy question or request, contact {entity} at{" "}
          <ContactLine />
          {has(address) ? `, or by post at ${address}` : ""}.
        </p>
      ),
    },
  ];

  return (
    <LegalLayout
      title="Privacy Policy"
      path="/privacy-policy/"
      description={`How ${entity} collects, uses, and protects your personal information under the Privacy Act 1988 and the Australian Privacy Principles.`}
      lastUpdated={LAST_UPDATED}
      intro={`Your privacy matters to us. This policy explains how we handle your personal information, including the sensitive information involved in a visa application.`}
      sections={sections}
    />
  );
}
